import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IVaultTeam, getAllTeams } from '../../api/vault/teams';
import { IAppState } from '../reducers';
import {
  create as createContact,
  getAll as getContactInfo,
  getByUserId as getContactByUserId,
  IContactInfo,
  _delete as deleteContactApi,
  update as updateContact,
} from '../../api/vault/contactInfo';
import { Validation } from '../../constants/validation';
import { validationReducer } from '../../utils/validation';
import { KeyRoleUserVaultRoles } from '../user/userOrgRole';
import { ISecurityProfile } from '../../api/vault/securityProfiles';

export enum ValidationKey {
  Email = 'NotificationSettingsEmail',
  Phone = 'NotificationSettingsPhone',
  FullName = 'NotificationSettingsFullName',
  JobTitle = 'NotificationSettingsJobTitle',
}

export const ValidationRules = {
  Email: [Validation.NOT_EMPTY, Validation.EMAIL],
  Phone: [Validation.PHONE],
  FullName: [Validation.NOT_EMPTY, Validation.FULLNAME],
};

export interface IState {
  isOpen: boolean;
  isLoaded: boolean;
  contacts: Array<IContactInfoProcessed>;
  newUser: boolean;
  userId: number;
  userOrgId: number;
  userTeamName: string;
  validationErrors: IValidationErrors;
  selected: ISelected;
  feedback: IFeedback;
}

export enum ActionType {
  Toggle = 'ContactInfoDialog.Toggle',
  ToggleLoaded = 'ContactInfoDialog.ToggleLoaded',
  Update = 'ContactInfoDialog.Update',
  ClearAll = 'ContactInfoDialog.ClearAll',
  UpdateErrors = 'ContactInfoDialog.UpdateErrors',
  ResetNewUser = 'ContactInfoDialog.ResetNewUser',
  UpdatedSelected = 'ContactInfoDialog.UpdatedSelected',
  RefreshContacts = 'ContactInfoDialog.RefreshContacts',
  UpdateFeedback = 'ContactInfoDialog.UpdateFeedback',
}

export interface IContactInfoDialogAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoaded: false,
  contacts: [],
  newUser: false,
  userId: null,
  userOrgId: null,
  userTeamName: '',
  validationErrors: {
    phoneNumber: '',
    email: '',
    fullName: '',
  },
  selected: {
    person: null,
    team: null,
    edit: null,
  },
  feedback: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: IContactInfoDialogAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.ToggleLoaded:
      return {
        ...state,
        isLoaded: !state.isLoaded,
      };
    case ActionType.UpdatedSelected:
      return {
        ...state,
        selected: action.payload.selected,
      };
    case ActionType.UpdateErrors:
      return {
        ...state,
        validationErrors: action.payload.validationErrors,
      };
    case ActionType.UpdateFeedback:
      return {
        ...state,
        feedback: action.payload.feedback,
      };
    case ActionType.RefreshContacts:
      return {
        ...state,
        contacts: action.payload.contacts,
      };
    case ActionType.Update:
      return {
        ...state,
        isLoaded: true,
        contacts: action.payload.contacts,
        newUser: action.payload.newUser,
        userId: action.payload.userId,
        userOrgId: action.payload.userOrgId,
        userTeamName: action.payload.userTeamName,
        selected: action.payload.selected,
      };
    case ActionType.ResetNewUser:
      return {
        ...state,
        newUser: false,
      };
    case ActionType.ClearAll:
      return {
        ...INITIAL_STATE,
      };
    default:
      return state;
  }
}

export const toggleContactInfoDialog = () => ({
  type: ActionType.Toggle,
});

export const clearAll = () => ({
  type: ActionType.ClearAll,
});

export enum TeamPerson {
  team = 'team',
  person = 'person',
}

export interface IContactInfoProcessed {
  teamPerson: TeamPerson,
  title: string,
  subtitle: string,
  contactData?: IContactInfo,
  _id?: string,
  peopleOnTeam?: string[],
  teamName?: string,
  imgSrc: string,
}

export interface IValidationErrors {
  email: string,
  phoneNumber: string,
  fullName: string,
}

export interface IFeedback {
  failed: boolean,
  message: string,
}

export interface ISelected {
  person: IContactInfoProcessed,
  team: IContactInfoProcessed,
  edit: IContactInfoProcessed,
}

const getTeamFromOrgId = (securityProfiles: ISecurityProfile[], teams: IVaultTeam[], orgId: number): IVaultTeam => {
  const securityProfile = securityProfiles.find((secProf) => secProf.idm_org_id === orgId);
  if (securityProfile) {
    return teams.find((team) => team._id === securityProfile.team_id);
  }
  return null;
};

const getContacts = async (securityProfiles: ISecurityProfile[], teams: IVaultTeam[]): Promise<IContactInfoProcessed[]> => {
  const { data: { data: contactData } } = await getContactInfo(null);
  const contacts: IContactInfoProcessed[] = [];
  contactData.forEach((value) => {
    const team = getTeamFromOrgId(securityProfiles, teams, value.orgId);
    if (team) { // Don't show contact if not linked to team
      contacts.push({
        teamPerson: TeamPerson.person,
        title: value.fullName,
        subtitle: value.jobTitle ? `${value.jobTitle} - ${team.TeamName} (${team.TeamCode})` : `${team.TeamName} (${team.TeamCode})`,
        contactData: value,
        _id: value._id,
        imgSrc: '',
        teamName: team.TeamName,
      });

      const foundTeam = contacts.find((contact) => contact.teamPerson === TeamPerson.team && team._id === contact._id);
      if (!foundTeam) {
        contacts.push({
          teamPerson: TeamPerson.team,
          title: team.TeamName,
          subtitle: team.TeamCode,
          _id: team._id,
          peopleOnTeam: [value._id],
          imgSrc: team.TeamImage,
        });
      } else {
        foundTeam.peopleOnTeam.push(value._id);
      }
    }
  });

  return _.sortBy(contacts, ['title']);
};

const checkUserCreated = async (userId: number): Promise<boolean> => {
  const res = await getContactByUserId(userId);
  if (res.data.data) {
    return true;
  }
  return false;
};

export const initContacts = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const securityProfiles = state.securityProfiles?.idValueMapping ?? [];
  const { data: { data: teams } } = await getAllTeams();
  const userId = state.user?.userOrgRole?.loginAs?.userInfo?.userId;
  const userVaultRoles = state.user?.userOrgRole?.loginAs?.[KeyRoleUserVaultRoles];
  const userOrgId = userVaultRoles?.[0]?.orgId;
  const newUser = !(await checkUserCreated(userId));

  const contacts = await getContacts(securityProfiles, teams);

  const userTeam = getTeamFromOrgId(securityProfiles, teams, userOrgId);
  const userTeamName = userTeam.TeamName;

  let edit = null;
  if (newUser) {
    edit = {
      teamPerson: TeamPerson.person,
      imgSrc: '',
      title: '',
      subtitle: '',
      teamName: userTeamName,
      contactData: {
        fullName: '',
        email: '',
        phoneNumber: '',
        jobTitle: '',
        orgId: userOrgId,
        userId,
        _id: null,
      },
    };
  }

  dispatch({
    type: ActionType.Update,
    payload: {
      contacts,
      newUser,
      userOrgId,
      userId,
      userTeamName,
      selected: {
        person: null,
        team: null,
        edit,
      },
    },
  });
};

const getEmailError = (contact: IContactInfoProcessed) => {
  const emailErrors = validationReducer(contact.contactData.email, ValidationRules.Email);
  if (emailErrors.length !== 0) {
    return emailErrors[0];
  }
  return '';
};

const getPhoneError = (contact: IContactInfoProcessed) => {
  if (contact.contactData.phoneNumber === '') {
    return '';
  }
  const phoneErrors = validationReducer(contact.contactData.phoneNumber, ValidationRules.Phone);
  if (phoneErrors.length !== 0) {
    return phoneErrors[0];
  }
  return '';
};

const getFullnameError = (contact: IContactInfoProcessed) => {
  const fullNameErrors = validationReducer(contact.contactData.fullName, ValidationRules.FullName);
  if (fullNameErrors.length !== 0) {
    return fullNameErrors[0];
  }
  if (contact.contactData.fullName.split(' ').length !== 2) {
    return 'Please enter first and last name';
  }
  return '';
};

export const validateContact = (contact: IContactInfoProcessed, key: ValidationKey) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { exchange: { contactInfoDialog: { validationErrors } } } = await getState();

  switch (key) {
    case ValidationKey.Email:
      validationErrors.email = getEmailError(contact);
      break;
    case ValidationKey.Phone:
      validationErrors.phoneNumber = getPhoneError(contact);
      break;
    case ValidationKey.FullName:
      validationErrors.fullName = getFullnameError(contact);
      break;
    default:
      break;
  }

  dispatch({
    type: ActionType.UpdateErrors,
    payload: {
      validationErrors: { ...validationErrors },
    },
  });
};

const refreshContacts = async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const securityProfiles = getState().securityProfiles?.idValueMapping ?? [];
  const { data: { data: teams } } = await getAllTeams();
  const contacts = await getContacts(securityProfiles, teams);
  dispatch({
    type: ActionType.RefreshContacts,
    payload: {
      contacts,
    },
  });
  return contacts;
};

export const saveContact = (contact: IContactInfoProcessed) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const contactInfoDialog = state.exchange?.contactInfoDialog;
  const validationErrors = contactInfoDialog?.validationErrors ?? { email: '', phoneNumber: '', fullName: '' };
  const newUser = contactInfoDialog?.newUser ?? false;
  const currentUserId = contactInfoDialog?.userId;
  const selected = contactInfoDialog?.selected;

  validationErrors.email = getEmailError(contact);
  validationErrors.phoneNumber = getPhoneError(contact);
  validationErrors.fullName = getFullnameError(contact);

  if (validationErrors.email !== '' || validationErrors.phoneNumber !== '' || validationErrors.fullName !== '') {
    dispatch({
      type: ActionType.UpdateErrors,
      payload: {
        validationErrors: { ...validationErrors },
      },
    });
    return;
  }

  const phone = contact.contactData.phoneNumber.replace(/\D/g, '');
  const {
    contactData: {
      userId, fullName, jobTitle, email,
    },
  } = contact;

  dispatch({ type: ActionType.ToggleLoaded });

  try {
    let res;
    if (newUser && currentUserId === userId) {
      res = await createContact({
        userId,
        fullName,
        jobTitle,
        email,
        phoneNumber: phone,
      });
    } else {
      res = await updateContact(contact.contactData._id, {
        userId,
        fullName,
        jobTitle,
        email,
        phoneNumber: phone,
      });
    }

    const updatedContacts = await refreshContacts(dispatch, getState);
    const updatedContact = updatedContacts.find((val) => val.teamPerson === TeamPerson.person && val.contactData.userId === userId);

    dispatch({ type: ActionType.ResetNewUser });
    dispatch({
      type: ActionType.UpdatedSelected,
      payload: {
        selected: {
          person: updatedContact,
          team: selected.team,
          edit: null,
        },
      },
    });
    dispatch({
      type: ActionType.UpdateFeedback,
      payload: {
        feedback: {
          failed: false,
          message: newUser ? 'Success! Contact has been created.' : 'Success! Contact has been updated.',
        },
      },
    });
  } catch (e) {
    const eType = e as any;
    let messageForm = 'Form failed to submit. Try Again';
    if (eType?.response?.data?.message?.includes('E11000 duplicate key error collection:')) {
      messageForm = 'Duplicate email entered. Use a different email';
    }
    dispatch({
      type: ActionType.UpdateFeedback,
      payload: {
        feedback: {
          failed: true,
          message: messageForm,
        },
      },
    });
  }

  dispatch({ type: ActionType.ToggleLoaded });
};

export const resetCreateContact = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ResetNewUser, payload: {},
  });
};

export const deleteContact = (id: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { exchange: { contactInfoDialog } } = getState();

  dispatch({ type: ActionType.ToggleLoaded });
  try {
    await deleteContactApi(id);
    await refreshContacts(dispatch, getState);
    dispatch({
      type: ActionType.UpdatedSelected,
      payload: {
        ...contactInfoDialog,
        selected: {
          person: null,
          team: null,
          edit: null,
        },
      },
    });
  } catch (e) {
    dispatch({
      type: ActionType.UpdateFeedback,
      payload: {
        feedback: {
          failed: true,
          message: 'Form failed to submit. Try Again',
        },
      },
    });
  }

  dispatch({ type: ActionType.ToggleLoaded });
};

export const updateSelected = (selected: ISelected) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.UpdatedSelected,
    payload: { selected },
  });
};

export const clearFeedback = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.UpdateFeedback,
    payload: {
      feedback: null,
    },
  });
};
